/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext,
    IRequestContext
} from '@msdyn365-commerce/core';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getAttributeValuesAsync, getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    ArrayExtensions,
    generateCacheKey,
    generateImageUrl,
    getProductUrlSync,
    getSelectedProductIdFromActionInput,
    QueryResultSettingsProxy,
    removeDomainQspFromUrl
} from '@msdyn365-commerce-modules/retail-actions';

import { IPageSummaryData } from '@msdyn365-commerce-modules/page-summary';
import { IProductPageSummaryConfig } from './product-page-summary.props.autogenerated';

/**
 * Product Page Summary Input.
 */
export class ProductPageSummaryInput implements IActionInput {
    public apiSettings: ICommerceApiSettings;

    public config: IProductPageSummaryConfig;

    public productId: number;

    public catalogId?: number;

    public constructor(
        config: IProductPageSummaryConfig,
        apiSettings: ICommerceApiSettings,
        productId: number,
        requestContext?: IRequestContext
    ) {
        this.config = config || {};
        this.apiSettings = apiSettings;
        this.productId = productId;

        if (requestContext) {
            this.catalogId = getCatalogId(requestContext);
        }
    }

    public getCacheObjectType = (): string => 'ProductPageSummary';

    public getCacheKey = (): string =>
        generateCacheKey(
            this.apiSettings,
            { key: 'Key', value: 'ProductPageSummary' },
            { key: 'Title', value: this.config.title },
            { key: 'ProductId', value: this.productId },
            { key: 'CatalogId', value: this.catalogId }
        );

    public dataCacheType = (): CacheType => 'application';
}

export interface IProductPageSummary extends IPageSummaryData {
    price?: number;
    productId?: number;
    productBrand?: string;
    itemId?: string;
}

const createInput = (args: ICreateActionContext) => {
    const productId = getSelectedProductIdFromActionInput(args);

    if (productId) {
        return new ProductPageSummaryInput(
            <IProductPageSummaryConfig>args.config,
            args.requestContext.apiSettings,
            +productId,
            args.requestContext
        );
    }
    throw new Error('Unable to create ProductPageSummaryAction input, no productId found on module config or query');
};

// Checks product attributes for the presence of brand attribute to extract the brand name for product metadata
const getProductBrand = async (
    context: IActionContext,
    recordId: number,
    channelId: number,
    catalogId: number
): Promise<string | undefined> => {
    const productAttributes = await getAttributeValuesAsync(
        {
            callerContext: context,
            queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context)
        },
        recordId,
        channelId,
        catalogId
    );
    for (const productAttribute of productAttributes) {
        if (productAttribute.Name === 'Brand') {
            return productAttribute.TextValue;
        }
    }
    return;
};

const action = async (input: ProductPageSummaryInput, context: IActionContext): Promise<IProductPageSummary> => {
    const { config, productId, apiSettings } = input;
    let simpleProductsData: SimpleProduct[] | undefined;
    try {
        simpleProductsData = ArrayExtensions.validValues(
            await getByIdsAsync(
                {
                    callerContext: context,
                    queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context)
                },
                apiSettings.channelId,
                [productId],
                null,
                input.catalogId ?? 0
            )
        );
    } catch {
        // Do nothing, if there's an error we fall back to values defined from config
    }
    if (ArrayExtensions.hasElements(simpleProductsData)) {
        const simpleProductData = simpleProductsData[0];
        let productUrl: string | undefined;
        try {
            productUrl = getProductUrlSync(simpleProductData, context);
            const canonicalDomain = context.requestContext.canonicalDomain;
            if (productUrl && canonicalDomain) {
                productUrl = `https://${canonicalDomain}${productUrl}`.toLocaleLowerCase();
                productUrl = removeDomainQspFromUrl(productUrl, context.requestContext);
            } else {
                productUrl = undefined;
            }
        } catch {
            productUrl = undefined;
        }
        let productBrand: string | undefined;
        try {
            productBrand = await getProductBrand(context, productId, apiSettings.channelId, input.catalogId ?? 0);
        } catch {
            productBrand = undefined;
        }
        return {
            title: simpleProductData.Name,
            description: simpleProductData.Description,
            sharingImageUrl: generateImageUrl(simpleProductData.PrimaryImageUrl, apiSettings),
            canonicalUrl: productUrl,
            faviconUrl: config && config.faviconUrl,
            productId: simpleProductData.RecordId,
            price: simpleProductData.AdjustedPrice,
            productBrand,
            itemId: simpleProductData.ItemId
        };

        // If the action fails fallback to values defined from config
    } else if (config) {
        return {
            title: config.title,
            description: config.description,
            sharingImageUrl: config.sharingImage && config.sharingImage.src,
            faviconUrl: config && config.faviconUrl
        };
    }
    return {};
};

export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/page-summary/product-page-summary',
    action: <IAction<IProductPageSummary>>action,
    input: createInput
});
